/**
 * KPI strip on the customers list (S09) — composes the shared `<StatTile>`.
 *
 * 4 tiles: Total / New this month / Avg payment time / Top exposure. The
 * tile shape comes from `features/master-list`; this file owns only the
 * customer-specific labels, tones, and "wires up with X" copy. Vendors
 * carries its own `kpi-strip.tsx` with its own four signals.
 *
 * @see prototype/sales/customers.html `.kpis` — V1.1 baseline
 */
import { Users as UsersIcon, TrendingUp, Timer, TriangleAlert } from "lucide-react";
import { StatTile } from "../../master-list/index.ts";
import { paiseToInrShort } from "../../../lib/format.ts";

export interface KpiStripProps {
  total: number;
  archived: number;
  /** Subset of `total` added in the last 30 days. */
  newThisMonth: number;
  topExposurePaise: string;
}

export function KpiStrip({ total, archived, newThisMonth, topExposurePaise }: KpiStripProps) {
  const hasExposure = BigInt(topExposurePaise) > 0n;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
      <StatTile
        label="Total customers"
        value={total.toLocaleString("en-IN")}
        meta={archived > 0 ? `${archived.toLocaleString("en-IN")} archived` : "on file"}
        tone="sky"
        icon={UsersIcon}
      />
      <StatTile
        label="New this month"
        value={newThisMonth.toLocaleString("en-IN")}
        meta="added in last 30d"
        tone="ok"
        icon={TrendingUp}
      />
      {/* TODO V1.0.x — wire to receipts.paid_at − invoices.issued_on average. */}
      <StatTile
        label="Avg payment time"
        value="—"
        meta="wires up with invoices + receipts"
        tone="warn"
        icon={Timer}
        placeholder
      />
      <StatTile
        label="Top exposure"
        value={hasExposure ? paiseToInrShort(topExposurePaise) : "—"}
        meta={hasExposure ? "largest open customer balance" : "no open exposure"}
        tone="bad"
        icon={TriangleAlert}
        placeholder={!hasExposure}
      />
    </div>
  );
}
